import { useState } from 'react';
import { t } from '@lingui/core/macro';
import { Plural, Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button/Button';
import Stack from '@/design_system/Stack';
import Tooltip from '@/design_system/Tooltip';
import InlineText from '@/design_system/Typography/InlineText';
import IconDone from '@/icons/Done.svg';
import IconEdit from '@/icons/Edit.svg';
import IconRefusal from '@/icons/Refusal.svg';
import IconTrash from '@/icons/Trash.svg';
import IconUndo from '@/icons/Undo.svg';
import IconValidation from '@/icons/Validation.svg';
import { TEMPORARY_ID_PREFIX, useDeleteDefect, useResetDefect } from '@/models/article';
import { isCustom, useDefectName } from '@/models/defectType';
import { ArticleDefectWithRelations } from '@/models/request';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { createBEMClasses } from '@/utils/classname';
import useViewPort from '@/utils/useViewport';

import { CreateEditDefectDialog } from '../CreateEditDefectDialog';

import { DefectAddedByBadge } from './DefectAddedByBadge';
import { DefectCustomDescriptionInput } from './DefectCustomDescriptionInput';
import { shouldShowExceptionalCoverageToggle } from './ExceptionalCoverageToggle';
import { getEditedFields } from './getEditedFields';

import './DefectsCard.css';

const { element } = createBEMClasses('defect-card');

export const DefectCardHeader = ({
  defect,
  previousDefect,
  duplicateDefectNumber,
}: {
  defect?: ArticleDefectWithRelations;
  previousDefect?: ArticleDefectWithRelations;
  duplicateDefectNumber?: number;
}) => {
  const { isMobile } = useViewPort();
  const { view, article } = useArticleContext();

  const exceptionalCoverageToggleShown = shouldShowExceptionalCoverageToggle({
    view,
    defect,
    article,
  });

  const isDefectEditable =
    !!defect &&
    ((isCustom(defect) &&
      view.defects.fields.description.editable &&
      view.defects.fields.photo.shown &&
      view.defects.fields.photo.editable) ||
      (view.defects.fields.context.shown && view.defects.fields.context.editable) ||
      (view.defects.fields.toBeRepaired.shown && view.defects.fields.toBeRepaired.editable) ||
      (view.defects.fields.brandResponsibility.enabled &&
        view.defects.fields.brandResponsibility.toggle.shown &&
        view.defects.fields.brandResponsibility.toggle.editable) ||
      (exceptionalCoverageToggleShown &&
        view.defects.fields.brandResponsibility.exceptionalCoverage.toggle.editable));

  const isDefectUndeletable = !defect && !!previousDefect && view.defects.canAddRemoveDefect;

  const showEditButton = isDefectEditable && isMobile;
  const showResetButton = isDefectUndeletable || (!!previousDefect && isDefectEditable);
  const showDeleteButton =
    !!defect && !defect.articleConditionId && view.defects.canAddRemoveDefect;

  return (
    <Stack
      row
      gap="1rem"
      alignItems="flex-start"
      justifyContent="space-between"
      flexWrap="nowrap"
      padding="12px"
      className={element('name', {
        edited: getEditedFields({ defect, previousDefect }).isNameEdited,
      })}
    >
      <Stack style={{ flex: 1, alignSelf: 'center' }} gap="0.25rem">
        <DefectName
          defect={defect}
          previousDefect={previousDefect}
          duplicateDefectNumber={duplicateDefectNumber}
        />
        {!isMobile && <DefectAddedByBadge defect={defect} previousDefect={previousDefect} />}
      </Stack>
      {(showEditButton || showResetButton || showDeleteButton) && (
        <Stack row gap="0.5rem" alignItems="center" flexWrap="nowrap">
          {showEditButton && <EditDefectButton defect={defect} />}
          {showResetButton && <ResetDefectButton defect={defect} previousDefect={previousDefect} />}
          {showDeleteButton && <DeleteDefectButton defect={defect} />}
        </Stack>
      )}
    </Stack>
  );
};

const DefectName = ({
  defect,
  previousDefect,
  duplicateDefectNumber,
}: {
  defect?: ArticleDefectWithRelations;
  previousDefect?: ArticleDefectWithRelations;
  duplicateDefectNumber?: number;
}) => {
  const { isMobile } = useViewPort();
  const { errors, view } = useArticleContext();

  const defectOrPreviousDefect = defect ?? previousDefect!;

  const defectName = useDefectName(defectOrPreviousDefect);

  const isMissingDescription = !!errors.defects?.defects?.find((def) => defect?.id === def.id)
    ?.missingDescription;

  const trimmedCustomDescription = (defectOrPreviousDefect.customDescription || '-').trim();

  const showInput =
    !!defect &&
    isCustom(defect) &&
    view.defects.fields.description.editable &&
    !defect.id.startsWith(TEMPORARY_ID_PREFIX) &&
    !isMobile;

  if (showInput) {
    return (
      <>
        <DefectCustomDescriptionInput defect={defect} />
        {!!defect && <DefectAssociatedActions defect={defect} />}
      </>
    );
  }

  return (
    <div>
      <Stack
        row
        gap=".5em"
        alignItems="center"
        style={isMissingDescription ? { color: 'var(--color-danger-700)' } : undefined}
      >
        {view.defects.fields.brandResponsibility.enabled && defect?.brandResponsibility && (
          <Tooltip
            content={t({
              id: 'defect-card.brand-responsibility.true',
              message: `Under brand responsibility`,
            })}
          >
            <Button variant="style-less">
              <IconValidation className="color-primary-700" style={{ fontSize: '1.5rem' }} />
            </Button>
          </Tooltip>
        )}
        {view.defects.fields.brandResponsibility.enabled &&
          !defect?.brandResponsibility &&
          defect?.removesAllBrandResponsibility && (
            <Tooltip
              content={t({
                id: 'defect-card.brand-responsibility.removes-all',
                message: `Cancels brand responsibility for all defects`,
              })}
            >
              <Button variant="style-less">
                <IconRefusal className="text-danger" style={{ fontSize: '1.5rem' }} />
              </Button>
            </Tooltip>
          )}
        <InlineText weight="medium">
          {isCustom(defectOrPreviousDefect) ? trimmedCustomDescription : defectName}{' '}
          {!!duplicateDefectNumber && `#${duplicateDefectNumber}`}
        </InlineText>
      </Stack>
      {!!defect && <DefectAssociatedActions defect={defect} />}
    </div>
  );
};

const EditDefectButton = ({ defect }: { defect: ArticleDefectWithRelations }) => {
  const { t } = useLingui();

  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Button
        variant="secondary"
        size="medium"
        iconOnly
        tooltip={t({ id: 'defect-card.edit', message: 'Edit' })}
        ariaLabel={t({ id: 'defect-card.edit', message: 'Edit' })}
        disabled={defect.id.startsWith(TEMPORARY_ID_PREFIX)}
        onPress={() => setIsOpen(true)}
      >
        <IconEdit />
      </Button>
      <CreateEditDefectDialog isOpen={isOpen} setIsOpen={setIsOpen} defect={defect} mode="edit" />
    </>
  );
};

const ResetDefectButton = ({
  defect,
  previousDefect,
}: {
  defect?: ArticleDefectWithRelations;
  previousDefect?: ArticleDefectWithRelations;
}) => {
  const { article, request } = useArticleContext();
  const { t } = useLingui();

  const { mutateAsync: resetDefect } = useResetDefect({
    requestId: request.id,
    articleId: article.id,
  });

  return (
    <Button
      variant="secondary"
      size="medium"
      iconOnly
      tooltip={t({ id: 'defect-card.undo', message: 'Undo changes' })}
      ariaLabel={t({ id: 'defect-card.undo', message: 'Undo changes' })}
      disabled={defect?.id.startsWith(TEMPORARY_ID_PREFIX)}
      onPress={() => {
        resetDefect((defect ?? previousDefect)!.stableId);
      }}
    >
      <IconUndo />
    </Button>
  );
};

const DeleteDefectButton = ({ defect }: { defect: ArticleDefectWithRelations }) => {
  const { article, request } = useArticleContext();
  const { t } = useLingui();

  const { mutateAsync: deleteDefect } = useDeleteDefect({
    requestId: request.id,
    articleId: article.id,
  });

  return (
    <Button
      variant="secondary"
      size="medium"
      iconOnly
      tooltip={t({ id: 'defect-card.deletion', message: 'Remove' })}
      ariaLabel={t({ id: 'defect-card.deletion', message: 'Remove' })}
      disabled={defect.id.startsWith(TEMPORARY_ID_PREFIX)}
      onPress={() => {
        if (
          defect.media.length > 0 &&
          !confirm(
            t({
              id: 'defect-card.deletion.confirmation',
              message:
                'Are you sure you want to delete this defect? The associated photos will be deleted as well.',
            })
          )
        ) {
          return;
        }

        deleteDefect(defect.id);
      }}
    >
      <IconTrash />
    </Button>
  );
};

const DefectAssociatedActions = ({ defect }: { defect: ArticleDefectWithRelations }) => {
  const nbAssociatedActions = defect?.articleActionToArticleDefects?.length ?? 0;

  if (!nbAssociatedActions) {
    return null;
  }

  return (
    <Stack row gap="0.25rem" alignItems="center" className="text-success">
      <IconDone style={{ fontSize: '1rem' }} />
      <InlineText size="200">
        <Trans id="defect-card.defect.actions-associated">
          {nbAssociatedActions}{' '}
          <Plural value={nbAssociatedActions} one="action associated" other="actions associated" />
        </Trans>
      </InlineText>
    </Stack>
  );
};
